import { Button, Grid, P } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import { useFormikContext } from 'formik'
import get from 'lodash/get'

import store from 'state/create-store'
import { showModal } from 'state/actions'
import namespace from 'lib/formik/namespace'
import { PROBATE_ESTATE_ITEM_TYPES } from 'lib/models/probate-estate'
import { NOT_ASKED_OPTIONS } from 'utils/enums'
import EstateCard from 'components/estate-card'
import InputFloating from 'components/form/input-floating'
import NotesInput from 'components/notes-input'
import CircularRadioGroup from 'components/form/circular-radio-group'
import DeleteModal from 'routes/lead/probate-form/delete-modal'
import withForm from 'routes/lead/probate-form/with-form'
import TextSpacer from 'routes/lead/probate-form/text-spacer'
import {
  PROBATE_LEAD_FORM_SECTIONS,
  PROBATE_LEAD_FORM_VALUES,
} from 'routes/lead/probate-form/constants'
import { getLifeInsuranceTotals } from 'routes/probate-estate/fact-find/get-totals'

import { HandleSaveProps, ItemProps } from './types'
import { useItems } from '../helpers/items-context'
import { useProbateEstate } from '../helpers/probate-estate-context'
import SubsectionHeading from '../subsection-heading'

const Item = ({ item, index, listLength }: ItemProps) => {
  const {
    DECEASED_SHARE_OF_JOINT_OWNERSHIP,
    DESCRIPTION,
    JOINT_OWNER_NAME,
    OWNERSHIP,
    VALUE,
    IS_INSURANCE_PAID_OUT,
    LUMP_SUM_PAYABLE_TO,
    NOTES,
  } = PROBATE_LEAD_FORM_VALUES
  const { withNamespace, withoutNamespace } = namespace(
    `lifeInsurances.${item.id}.`
  )
  const { values } = useFormikContext()
  const { updateItem, deleteItem } = useItems()

  const handleSave = ({ name, value }: HandleSaveProps) => {
    updateItem(
      {
        id: item.id,
        attributes: { [withoutNamespace(name)]: value || null },
      },
      PROBATE_ESTATE_ITEM_TYPES.LIFE_INSURANCE
    )
  }

  const onTrashClick = () => {
    store.dispatch(
      showModal({
        component: DeleteModal,
        headingText: 'Delete life insurance',
        config: {
          deleteCallback: () => {
            deleteItem(item.id)
          },
          type: 'life insurance',
          resourceId: item.id,
        },
      })
    )
  }

  return (
    <EstateCard
      onTrashClick={onTrashClick}
      listLength={listLength}
      listPosition={index + 1}
      key={item.id}
      type="life insurance"
    >
      <Grid style={{ gridAutoFlow: 'row' }}>
        <Grid.Item>
          <InputFloating
            name={withNamespace(DESCRIPTION)}
            label="Provider and description"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item span={6}>
          <InputFloating
            name={withNamespace(VALUE)}
            label="Total value (£)"
            hint="Add the total value. For joint assets, the £ value of their share will be calculated automatically."
            handleSave={handleSave}
            isCurrency
          />
        </Grid.Item>
        <Grid.Item>
          <CircularRadioGroup
            name={withNamespace(OWNERSHIP)}
            label="Ownership"
            options={[
              { label: 'Not asked', value: '' },
              { label: 'Sole', value: 'sole' },
              { label: 'Joint', value: 'joint' },
            ]}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>

        {get(values, withNamespace(OWNERSHIP)) === 'joint' && (
          <>
            <Grid.Item span={6}>
              <InputFloating
                name={withNamespace(JOINT_OWNER_NAME)}
                label="Name of joint owner"
                handleSave={handleSave}
                hint="If more than one, leave a note below"
              />
            </Grid.Item>
            <Grid.Item span={6}>
              <InputFloating
                name={withNamespace(DECEASED_SHARE_OF_JOINT_OWNERSHIP)}
                label="Share owned by person who's died (%)"
                handleSave={handleSave}
              />
            </Grid.Item>
          </>
        )}

        <Grid.Item>
          <CircularRadioGroup
            name={withNamespace(IS_INSURANCE_PAID_OUT)}
            label="Has this policy paid out?"
            options={NOT_ASKED_OPTIONS}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        <Grid.Item>
          <CircularRadioGroup
            name={withNamespace(LUMP_SUM_PAYABLE_TO)}
            label="Who is the lump sum payable to?"
            options={[
              { label: 'Not asked', value: '' },
              { label: 'The estate', value: 'estate' },
              {
                label: 'Written in trust / not part of estate for IHT reasons',
                value: 'not_part_of_estate',
              },
            ]}
            handleSave={handleSave}
            inline
          />
        </Grid.Item>
        <Grid.Item>
          <NotesInput name={withNamespace(NOTES)} handleSave={handleSave} />
        </Grid.Item>
      </Grid>
    </EstateCard>
  )
}

const ItemWithForm = withForm(Item)

const LifeInsurance = ({ isComplete }: { isComplete: boolean }) => {
  const { LIFE_INSURANCE } = PROBATE_LEAD_FORM_SECTIONS
  const { saveProbateEstateField } = useProbateEstate()
  const { groupedItems, createItem } = useItems()
  const { lifeInsurance: lifeInsuranceItems = [] } = groupedItems
  const { total, sole, joint, unspecified } =
    getLifeInsuranceTotals(lifeInsuranceItems)

  return (
    <SubsectionHeading
      disabled={lifeInsuranceItems.length > 0}
      name={LIFE_INSURANCE}
      onToggle={saveProbateEstateField}
      scrollId="6.4"
      title="Life insurance"
      isComplete={isComplete}
    >
      <Grid.Item span={11}>
        <P size="S" data-testid="items-value-summary">
          Total: {total} <TextSpacer /> Sole: {sole} <TextSpacer /> Joint share:{' '}
          {joint} <TextSpacer /> Unspecified: {unspecified}
        </P>

        {lifeInsuranceItems.map((item, index) => (
          <ItemWithForm
            enableReinitialize={false}
            index={index}
            initialValues={{ lifeInsurances: { [item.id]: item.attributes } }}
            item={item}
            key={item.id}
            listLength={lifeInsuranceItems.length}
          />
        ))}
      </Grid.Item>
      <Grid.Item>
        <Button.Secondary
          style={{ paddingTop: GTR.XXS, paddingBottom: GTR.XXS }}
          type="button"
          onClick={() => {
            createItem({ itemType: PROBATE_ESTATE_ITEM_TYPES.LIFE_INSURANCE })
          }}
        >
          Add life insurance
        </Button.Secondary>
      </Grid.Item>
    </SubsectionHeading>
  )
}

export default LifeInsurance
